/**
 * Stereo Image Skew geometry — inter-ear level/time differences vs.
 * frequency, computed from two complex frequency responses (left/right ear
 * offset positions, see `useStereoImageSkew`). Reveals room-induced stereo
 * image asymmetry: a room that's acoustically symmetric left-right should
 * show both curves hovering near zero across the band; a persistent
 * levelDiff or timeDiff at a given frequency means that frequency images
 * off-center even with a perfectly panned source.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, type LinearScale, type LogFreqScale } from './scales';
import { dbViewportFromSeries, mapSeriesToPlot, type PlotPoint } from './series';
import { linearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';

export interface StereoSkewPoint {
  freqHz: number;
  levelDiffDb: number; // right - left, positive = image pulls right
  timeDiffMs: number; // derived from phase difference: dt = dphi/(2*pi*f)
  x: number;
  y: number;
}

export interface StereoImageSkewInput {
  freqs: number[];
  magLeft: number[];
  magRight: number[];
  phaseLeft: number[];
  phaseRight: number[];
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

export interface StereoImageSkewGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  /** Left axis: level-difference (dB) scale + the points/ticks drawn against it. */
  yScaleLevel: LinearScale;
  levelPoints: PlotPoint[];
  yTicksLevel: YTick[];
  /** Right axis: time-difference (ms) scale + the points/ticks drawn against it. */
  yScaleTime: LinearScale;
  timePoints: PlotPoint[];
  yTicksTime: YTick[];
  xTicks: AxisTicks;
  points: StereoSkewPoint[];
}

function emptyGeometry(frame: PlotFrame, fMin: number, fMax: number, xScale: LogFreqScale, xTicks: AxisTicks): StereoImageSkewGeometry {
  return {
    empty: true, frame, fMin, fMax, xScale,
    yScaleLevel: createLinearScale(frame.plotH, -6, 6, true),
    levelPoints: [], yTicksLevel: [],
    yScaleTime: createLinearScale(frame.plotH, -1, 1, true),
    timePoints: [], yTicksTime: [],
    xTicks, points: [],
  };
}

/** dt = dphi / (2*pi*f), seconds -> ms. Positive means the right ear's
 *  arrival lags the left ear's (phaseRight ahead of phaseLeft). */
function timeDiffMs(freq: number, phaseLeftRad: number, phaseRightRad: number): number {
  if (!(freq > 0)) return 0;
  const dPhi = phaseRightRad - phaseLeftRad;
  return (dPhi / (2 * Math.PI * freq)) * 1000;
}

export function computeStereoImageSkewGeometry(opts: StereoImageSkewInput): StereoImageSkewGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 20000;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const n = opts.freqs.length;
  const lengthsMatch = opts.magLeft.length === n && opts.magRight.length === n
    && opts.phaseLeft.length === n && opts.phaseRight.length === n;

  if (n === 0 || !lengthsMatch || frame.plotW <= 0) {
    return emptyGeometry(frame, fMin, fMax, xScale, xTicks);
  }

  const levelDiffDb: number[] = new Array(n);
  const timeDiff: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const ml = opts.magLeft[i]!;
    const mr = opts.magRight[i]!;
    levelDiffDb[i] = ml > 0 && mr > 0 ? 20 * Math.log10(mr / ml) : 0;
    timeDiff[i] = timeDiffMs(opts.freqs[i]!, opts.phaseLeft[i]!, opts.phaseRight[i]!);
  }

  const levelDomain = dbViewportFromSeries([levelDiffDb], { signalFloor: -Infinity, roundTo: 1, minSpanDb: 2 });
  const yScaleLevel = createLinearScale(frame.plotH, levelDomain.lo, levelDomain.hi, true);
  const levelPoints = mapSeriesToPlot({ x: opts.freqs, y: levelDiffDb }, xScale, yScaleLevel);

  const timeAbsMax = Math.max(1, ...timeDiff.map((v) => Math.abs(v)));
  const timeSpan = Math.ceil(timeAbsMax * 10) / 10;
  const yScaleTime = createLinearScale(frame.plotH, -timeSpan, timeSpan, true);
  const timePoints = mapSeriesToPlot({ x: opts.freqs, y: timeDiff }, xScale, yScaleTime);

  const levelStep = Math.max(1, Math.round((levelDomain.hi - levelDomain.lo) / 6));
  const yTicksLevel: YTick[] = linearTicks(levelDomain.lo, levelDomain.hi, levelStep).map((v) => ({
    value: v, y: linearToPlot(yScaleLevel, v), major: v === 0, label: `${v > 0 ? '+' : ''}${v.toFixed(0)}`,
  }));

  const timeStep = Math.max(0.1, Math.round((timeSpan * 2) / 6 * 10) / 10);
  const yTicksTime: YTick[] = linearTicks(-timeSpan, timeSpan, timeStep).map((v) => ({
    value: v, y: linearToPlot(yScaleTime, v), major: Math.abs(v) < 1e-9, label: `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
  }));

  // `mapSeriesToPlot` drops samples outside [fMin, fMax] (and non-finite
  // ones), so `levelPoints` is a SUBSEQUENCE of the input — walk it with its
  // own cursor rather than indexing it by `i`, or a frequency grid that runs
  // past fMax (a full-band response shown on a 20–200 Hz axis) misaligns
  // every point and reads past the end.
  const points: StereoSkewPoint[] = [];
  let k = 0;
  for (let i = 0; i < n; i++) {
    const f = opts.freqs[i]!;
    if (!Number.isFinite(f) || f < fMin || f > fMax || !Number.isFinite(levelDiffDb[i]!)) continue;
    const p = levelPoints[k++]!;
    points.push({
      freqHz: f,
      levelDiffDb: levelDiffDb[i]!,
      timeDiffMs: timeDiff[i]!,
      x: p.x,
      y: p.y,
    });
  }

  return {
    empty: false, frame, fMin, fMax, xScale,
    yScaleLevel, levelPoints, yTicksLevel,
    yScaleTime, timePoints, yTicksTime,
    xTicks, points,
  };
}

/** Given a listener seat position and the room's lateral (left-right) axis,
 *  returns the two ear positions offset by earOffsetM/2 each side —
 *  feeds the two frequency-response fetches (`useStereoImageSkew` on web,
 *  `useStereoImageSkewForCharts` on mobile) this chart's geometry consumes.
 *  Default earOffsetM=0.18 (average interaural distance ~18cm) is a coarse
 *  approximation, not a HRTF-accurate binaural model. */
export function earOffsetPositions(
  listenerPos: [number, number, number],
  lateralAxis: [number, number, number], // unit vector, room's left-right direction
  earOffsetM = 0.18,
): { left: [number, number, number]; right: [number, number, number] } {
  const half = earOffsetM / 2;
  return {
    left: [
      listenerPos[0] - lateralAxis[0] * half,
      listenerPos[1] - lateralAxis[1] * half,
      listenerPos[2] - lateralAxis[2] * half,
    ],
    right: [
      listenerPos[0] + lateralAxis[0] * half,
      listenerPos[1] + lateralAxis[1] * half,
      listenerPos[2] + lateralAxis[2] * half,
    ],
  };
}
